import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { StagLogo } from "../components/StagLogo";
import { Loader2, CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import { VAULT } from "../data/vaultStats";
import { trackEvent } from "../lib/analytics";
import { AffiliateInvite } from "../components/AffiliateInvite";

export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ stage: "polling", message: "Confirming your payment...", plan: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ stage: "error", message: "Missing session id.", plan: null });
      return;
    }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          await refresh();
          setState({ stage: "ok", message: "Payment confirmed.", plan: data.plan, byovSize: data.byov_size, byovCredit: data.byov_upgrade_credit });
          const purchase = { value: Number(data.amount || 0), currency: (data.currency || "gbp").toUpperCase(), transaction_id: sessionId, plan: data.plan, content_name: data.plan, content_ids: [data.plan] };
          trackEvent("Purchase", purchase, `purchase_${sessionId}`);
          trackEvent("ProductPurchased", { ...purchase, product: data.plan }, `product_${sessionId}`);
          return;
        }
        if (data.status === "expired") {
          setState({ stage: "error", message: "This payment session expired. Try again.", plan: null });
          return;
        }
        if (attempts >= 6) {
          setState({ stage: "pending", message: "Still processing. Refresh in a minute to confirm.", plan: null });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (cancelled) return;
        if (attempts >= 5) {
          try {
            const { data: me } = await api.get("/auth/me");
            if (me && me.tier && me.tier !== "free") {
              await refresh();
              setState({ stage: "ok", message: "Payment confirmed.", plan: me.tier === "starter" ? "starter" : null });
              return;
            }
          } catch {}
          setState({ stage: "error", message: "Could not confirm payment. Contact support if charged.", plan: null });
        } else {
          setTimeout(poll, 2500);
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  const isStarter = state.plan === "starter";
  const isPlaybook = state.plan === "playbook";
  const isByov = state.plan === "byov10" || state.plan === "byov15";
  const isCompleteUpgrade = state.plan === "lifetime" && Number(state.byovCredit) > 0;
  const trackSlug = typeof state.plan === "string" && state.plan.startsWith("track:") ? state.plan.slice(6) : null;
  const successDest = isByov ? "/my-vault" : trackSlug ? `/vault/${trackSlug}` : isPlaybook ? "/playbook/download" : isStarter ? "/starter-pack" : "/vault";
  const successMessage = isByov ? `Your Vault is built — Build ${state.byovSize || (state.plan === "byov15" ? 15 : 10)}.` : isCompleteUpgrade ? "Stag Vault Complete unlocked." : trackSlug ? "Track unlocked." : isPlaybook ? "Playbook unlocked." : isStarter ? "Starter Pack unlocked." : "You're in. Stag Vault Complete unlocked.";
  const successCta = isByov ? "Open My Vault" : trackSlug ? "Open my track" : isPlaybook ? "Download my Playbook" : isStarter ? "Open my Starter Pack" : "Enter the vault";
  const successBody = isByov
    ? `Lifetime access to your ${state.byovSize || (state.plan === "byov15" ? 15 : 10)} selected tracks is live on this account${Number(state.byovCredit) > 0 ? " — your existing tracks are kept and the upgrade credit was applied" : ""}. A confirmation email listing every track is on its way.`
    : isCompleteUpgrade
    ? `Upgrade complete. All ${VAULT.tracks} tracks are unlocked for life, your Build tracks are kept, and you only paid the difference.`
    : trackSlug
    ? "Your single track is unlocked on this account — every prompt is copy-ready and runs with AI inside the vault. A receipt has been emailed to you."
    : isPlaybook
    ? `All ${VAULT.tracks} tracks and ${VAULT.promptsFmt} prompts in Word, Markdown and plain text. Download it now — and re-download any time as the vault grows.`
    : isStarter
    ? "5 fastest-to-build AI product blueprints are ready. Pick one and ship your first product this weekend."
    : `All ${VAULT.tracks} tracks are unlocked. Run-with-AI is unlimited. Go build something.`;

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-6" data-testid="checkout-success-page">
      <div className="w-full max-w-lg text-center">
        <div className="text-purple-400 mb-8 flex justify-center"><StagLogo size={56} /></div>
        {state.stage === "polling" && (
          <>
            <Loader2 size={36} className="text-purple-400 animate-spin mx-auto mb-6" />
            <h1 className="font-display text-3xl font-bold tracking-tight mb-4" data-testid="checkout-status-polling">{state.message}</h1>
            <p className="text-zinc-400">This takes a few seconds.</p>
          </>
        )}
        {state.stage === "ok" && (
          <>
            <CheckCircle2 size={48} className="text-purple-400 mx-auto mb-6 drop-shadow-[0_0_25px_rgba(168,85,247,0.5)]" />
            <h1 className="font-display text-4xl font-black tracking-tighter mb-4" data-testid="checkout-status-ok">{successMessage}</h1>
            <p className="text-zinc-300 text-lg leading-relaxed mb-10" data-testid="checkout-success-body">{successBody}</p>
            <Link
              to={successDest}
              className="inline-flex items-center gap-3 bg-purple-500 hover:bg-purple-400 text-black px-8 py-4 text-sm tracking-[0.25em] uppercase font-bold transition-all duration-300 shadow-[0_0_25px_rgba(168,85,247,0.4)] hover:shadow-[0_0_45px_rgba(168,85,247,0.75)]"
              data-testid="checkout-success-cta"
            >
              {successCta}
              <ArrowRight size={18} />
            </Link>
            {!isStarter && !isPlaybook && <AffiliateInvite surface="checkout-success" className="mt-10 text-left" />}
            {isStarter && (
              <p className="mt-6 text-zinc-500 text-xs tracking-wide">
                Want all {VAULT.promptsFmt} prompts across {VAULT.tracks} tracks? <Link to="/pricing" className="text-purple-300 hover:text-purple-200 underline">Upgrade to Stag Vault Complete for £25 →</Link>
              </p>
            )}
          </>
        )}
        {(state.stage === "error" || state.stage === "pending") && (
          <>
            <XCircle size={40} className="text-zinc-500 mx-auto mb-6" />
            <h1 className="font-display text-3xl font-bold tracking-tight mb-4" data-testid="checkout-status-error">{state.message}</h1>
            <Link to="/pricing" className="text-purple-400 hover:text-purple-300 text-sm tracking-wide">← Back to pricing</Link>
          </>
        )}
      </div>
    </div>
  );
}
